import { LoadingOutlined, UploadOutlined, DeleteOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Col, Form, Row, Typography, Upload, Modal } from "antd";
import Arrow from 'assets/svg/arrow.svg';
import autoLeasing from "assets/svg/autoLeasing.svg";
import personalFinance from "assets/svg/personalFinance.svg";
import microFinance from "assets/svg/microFinance.svg";
import motoFinance from "assets/svg/motoFinance.svg";
import ivrPending from "assets/svg/ivrPending.svg";
import axios from "axios";
import { FormDrawer, buildInitialValues, buildValidationSchema } from 'Components';
import { useTranslation } from "contexts/TranslationContext";
import { Formik } from "formik";
import useAuthHook from "hooks/useAuthHook";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from "react-toastify";
import { useCreateNewAppMutation, useCreateNewTawaruqAppMutation, useCreateNewIjarahAppMutation, useGetLoanReasonsQuery, useUpdateAppDetailsMutation, useUpdateAppDetailsV2Mutation, useGetManufacturersQuery, useGetDealersLookupQuery, useGetColorsLookupQuery, useLazyGetAssetModelsQuery, useLazyGetAssetModelTypesQuery, useGetAssetConditionsQuery } from "services/auth";
import { getUpgradeFlag } from "services/lookupSelector";
import BackButton from 'shared/components/BackButton/BackButton';
import env from "shared/constants/env";
import routes, { appRoute } from 'shared/constants/routes';
import * as Yup from "yup";
import { getApiEndpoint } from "../../../utilities/apiEndpoint";
import SalaryEligibilityGate from "components/SalaryEligibility/SalaryEligibilityGate";

// import SilverVoucher from "assets/svg/SilverVoucher.svg";
// import EmeraldVoucher from "assets/svg/EmeraldVoucher.svg";



const { Title, Text } = Typography;
const { Dragger } = Upload;


const FinanceProducts = () => {
    const { translate, language } = useTranslation();
    const [createNewApp, createNewAppStatus] = useCreateNewAppMutation();
    const [createNewTawaruqApp, createNewTawaruqAppStatus] = useCreateNewTawaruqAppMutation();
    const [createNewIjarahApp, createNewIjarahAppStatus] = useCreateNewIjarahAppMutation();
    const [updateApplicationDetails, updateAppDetailsStatus] = useUpdateAppDetailsMutation();
    const [updateApplicationDetailsV2, updateAppDetailsStatusV2] = useUpdateAppDetailsV2Mutation();
    const { getUser } = useAuthHook();
    const navigate = useNavigate();
    const { id } = useParams();

    // Salary eligibility gate: id 2 is Tawarruq, id 3 Microfinance, and both
    // auto (1) and moto (4) are Ijarah.
    const eligibilityProductCode = id === "2" ? "TWQ" : id === "3" ? "MICRO" : "IJARAH";

    // Initialize isSaudiUser state from localStorage
    const [isSaudiUser, setIsSaudiUser] = useState(() => {
        const user = JSON.parse(localStorage.getItem("@@TAMWEEL_USER"));
        return user?.USER_ID?.toString().startsWith('1') || false;
    });

    const [userHasAccessV2, setUserHasAccessV2] = useState((id === '1' || id === '2' || id === '3' || id === '4')); //to check if user is from v1 or v2

    // Get loan reasons using the new lookup hook - only fetch when needed for personal finance
    const {
        data: loanReasonsData,
        error: loanReasonsError,
        isLoading: loanReasonsLoading
    } = useGetLoanReasonsQuery(
        //version 2 only for personal finance & micro finance 
        (id === "2" || id === "3") && { status: 'ACTIVE', apiVersion: "2" },
        {
            skip: id === "1"  // dont fetch for autoleasing (id === "1")
        }
    );

    // V2 Auto Lease lookup hooks - fetch independently (no chaining)
    const { data: manufacturersData, isLoading: manufacturersLoading } = useGetManufacturersQuery(
        { status: 'ACTIVE' },
        { skip: id !== '1' && id !== '4' }
    );
    const { data: dealersData, isLoading: dealersLoading } = useGetDealersLookupQuery(
        { status: 'ACTIVE' },
        { skip: id !== '1' && id !== '4' }
    );
    const { data: colorsData, isLoading: colorsLoading } = useGetColorsLookupQuery(
        undefined,
        { skip: id !== '1' && id !== '4' }
    );
    const [triggerGetAssetModels, { data: assetModelsData, isLoading: assetModelsLoading, isFetching: assetModelsFetching }] = useLazyGetAssetModelsQuery();
    const [triggerGetAssetModelTypes, { data: assetModelTypesData, isLoading: assetModelTypesLoading, isFetching: assetModelTypesFetching }] = useLazyGetAssetModelTypesQuery();
    const { data: assetConditionsData, isLoading: assetConditionsLoading } = useGetAssetConditionsQuery(
        undefined,
        { skip: id !== '1' && id !== '4' }
    );

    const [uploadFile, setUploadFile] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    // V2 Document selection tracking (before app creation)
    const [selectedBankStatement, setSelectedBankStatement] = useState(null);
    const [selectedPaySlip, setSelectedPaySlip] = useState(null);
    const [selectedOtherDocuments, setSelectedOtherDocuments] = useState(null);

    // V2 Individual file upload loading states (during upload phase)
    const [bankStatementUploading, setBankStatementUploading] = useState(false);
    const [paySlipUploading, setPaySlipUploading] = useState(false);
    const [otherDocumentsUploading, setOtherDocumentsUploading] = useState(false);

    const [currentAppId, setCurrentAppId] = useState(null);
    const [tenureOptions, setTenureOptions] = useState([]);
    const [termsChecked, setTermsChecked] = useState(true);
    const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
    const [manufacturerList, setManufacturerList] = useState([]);
    const [modelList, setModelList] = useState([]);
    const [modelTypeList, setModelTypeList] = useState([]);
    const [dealerList, setDealerList] = useState([]);
    const [colorList, setColorList] = useState([]);
    const [conditionList, setConditionList] = useState([]);
    const [yearList, setYearList] = useState([]);
    const [loanReasonsOptions, setLoanReasonsOptions] = useState([]);
    const [autoLoanLoading, setAutoLoanLoading] = useState(false);
    const [ibanVerifyLoader, setIbanVerifyLoader] = useState(false)
    const [isCreating, setIsCreating] = useState(false);
    const [isIvrModalVisible, setIsIvrModalVisible] = useState(false);
    const [ivrApplicationId, setIvrApplicationId] = useState(null);


    const [assetIds, setAssetIds] = useState([]);
    const [assetId, setAssetId] = useState(null)
    const formikRef = useRef(null);

    // Helper function to get filename from file object or path
    const getFileName = (file) => {
        if (typeof file === 'string') {
            // If it's a path string, extract filename
            return file.split('/').pop().split('\\').pop();
        }
        // If it's a File object, get the name directly
        return file.name || file.originFileObj?.name || '';
    };

    // Check if filename already exists in uploaded files
    const isDuplicateFileName = (newFileName) => {
        const newFileBaseName = newFileName.toLowerCase().trim();

        // For V1 users: Check against fileList
        if (id === '1') {
            return fileList.some(existingFile => {
                const existingFileName = getFileName(existingFile).toLowerCase().trim();
                return existingFileName === newFileBaseName;
            });
        }

        // For V2 users: Check against all selected documents
        if (userHasAccessV2 && (id === '2' || id === '3')) {
            const selectedFiles = [
                selectedBankStatement,
                selectedPaySlip,
                selectedOtherDocuments
            ].filter(file => file !== null);

            return selectedFiles.some(existingFile => {
                const existingFileName = getFileName(existingFile).toLowerCase().trim();
                return existingFileName === newFileBaseName;
            });
        }

        return false;
    };

    function generateYearArray(startYear) {
        const currentYear = new Date().getFullYear();
        const years = [];

        for (let year = startYear; year <= currentYear; year++) {
            years.push({ label: String(year), value: String(year) });
        }

        return years;
    }

    const APP_DETAILS_DTO_KEYS = ["IBAN", "gender", "maritalStatus", "nationality", "numberOfDependents", "title", "region", "city", "foodExpenses", "housingExpenses", "domesticWorkers", "educationExpenses", "healthCareExpenses", "commAndTransExpenses", "insuranceExpenses", "anyExpectedFutureExpense", "productId", "subproductId", "request_amount", "loanReason", "assetModelYear", "assetPrice", "downPayment", "ballonPayment", "dealerId", "colorId", "assetId", "salesRef", "tenure", "empCatId", "p_residentId", "qualificationId", "sectorId", "dateOfJoining", "lat", "long", "buildingNum", "streetName", "district_name", "additionalNum", "postCode", "pep", "beneficiary", "beneficiaryName", "relatedPartyBoolean", "partyId"];

    const filterObjectByKeys = (obj, keys) =>
        Object.keys(obj)
            .filter((key) => keys.includes(key))
            .reduce((data, key) => ({ ...data, [key]: obj[key] }), {});


    useEffect(() => {
        // Handle loan reasons data from the hook
        if ((id === "2" || id === "3") && loanReasonsData?.payload?.rows) {
            console.log('loan reasons', loanReasonsData?.payload?.rows, userHasAccessV2);
            setLoanReasonsOptions(loanReasonsData?.payload?.rows.map(row => ({
                label: String(row.DESCRIPTION),
                value: String(row.ID),
                labelAr: String(row.DESCRIPTIONAR),
            })));
        }

        // Handle errors
        if (loanReasonsError) {
            toast.error('Failed to load loan reasons.');
        }
    }, [id, loanReasonsData, loanReasonsError, userHasAccessV2]);


    useEffect(() => {
        //check for micro financing eligibility
        const user = JSON.parse(localStorage.getItem("@@TAMWEEL_USER"));
        if (id === '3' && user?.USER_ID && !user?.USER_ID?.toString().startsWith('1')) {
            toast.error('You are not eligible for Micro Financing.');
            navigate(appRoute(routes.HOME));
        }
    }, [id, navigate, translate]);



    const autoLeaseDetails =
    {
        id: 1,
        title: translate("HOME.AUTO"),
        description: "HOME.AUTO_DESC",
        image: autoLeasing,
        route: 1,
        subDescription: "PRODUCTS.AUTO_DESC_SUB",
    }


    const personalFinanceDetails =
    {
        id: 2,
        title: translate("HOME.PERSONAL"),
        description: "HOME.PERSONAL_DESC",
        image: personalFinance,
        route: 2,
        subDescription: "PRODUCTS.PERSONAL_DESC_SUB",
    }

    const microFinanceDetails =
    {
        id: 3,
        title: translate("HOME.MICRO"),
        description: "HOME.MICRO_DESC",
        image: microFinance,
        route: 3,
        subDescription: "PRODUCTS.MICRO_DESC_SUB",
    }

    const motoFinanceDetails =
    {
        id: 4,
        title: translate("HOME.MOTO"),
        description: "HOME.MOTO_DESC",
        image: motoFinance,
        route: 4,
        subDescription: "PRODUCTS.MOTO_DESC_SUB",
    }



    const showTermsModal = () => {
        setIsTermsModalVisible(true);
    };

    const handleTermsOk = () => {
        setIsTermsModalVisible(false);
    };

    const handleTermsCancel = () => {
        setIsTermsModalVisible(false);
    };
    // const { values } = useFormikContext()

    const isArabic = language?.code === "ar" ? true : false;

    let isLoading = createNewAppStatus.isLoading || createNewTawaruqAppStatus.isLoading || createNewIjarahAppStatus.isLoading || updateAppDetailsStatus.isLoading || autoLoanLoading || loanReasonsLoading || manufacturersLoading || dealersLoading || colorsLoading || assetModelsLoading || assetModelsFetching || assetModelTypesLoading || assetModelTypesFetching || assetConditionsLoading;

    // Check if any individual upload is in progress (V2 only)
    const isAnyFileUploading = userHasAccessV2 ? (bankStatementUploading || paySlipUploading || otherDocumentsUploading) : isUploading;

    const autoLeaseSchema = ({ hideRequestedLoanAmount = true, tenureOptions = [], manufacturerList = [], modelList = [], modelTypeList = [], dealerList = [], colorList = [], conditionList = [], yearList = [] }) => [
        {
            type: "select",
            key: "tenure",
            label: "Tenure",
            required: true,
            options: tenureOptions,
            // value: 4,
            span: 24,
            validation: Yup.string()
                .required("CANNOT_BE_EMPTY")
        },

        {
            type: "number",
            key: "salesRef",
            label: "SalesRef",
            span: 24,
            max: 10,
            min: 8,
            maxLength: 10,
            minLength: 8,
            required: false,
            validation: Yup.string()
                .matches(/^\d{8,10}$/, "MUST_BE_BETWEEN_8_AND_10_DIGITS")
        },

        {
            type: "select",
            key: "assetManufacturer",
            label: "Asset_manufacturer",
            searchable: true,
            span: 24,
            options: manufacturerList,
            required: true,
            validation: Yup.string().required("CANNOT_BE_EMPTY"),
        },
        {
            type: "select",
            key: "assetModel",
            label: "Asset_model",
            searchable: true,
            span: 24,
            options: modelList,
            required: true,
            validation: Yup.string().required("CANNOT_BE_EMPTY"),
        },
        {
            type: "select",
            key: "assetModelType",
            label: "Asset_model_type",
            searchable: true,
            span: 24,
            options: modelTypeList,
            required: true,
            validation: Yup.string().required("CANNOT_BE_EMPTY"),
        },
        {
            type: "select",
            key: "colorId",
            label: "Asset_color",
            span: 24,
            options: colorList,
            required: true,
        },
        {
            type: "select",
            key: "assetCondition",
            label: "Asset_condition",
            span: 24,
            required: true,
            options: conditionList,
            validation: Yup.string().required("CANNOT_BE_EMPTY"),

        },
        {
            type: "number",
            key: "assetPrice",
            label: "Asset_price",
            span: 24,
            required: true,
            validation: Yup.number().integer("MUST_BE_INTEGER").min(25000, "MUST_BE_ATLEAST").max(9999999, "CANNOT_EXCEED_7"),
        },
        {
            type: "select",
            key: "assetModelYear",
            label: "Asset_model_year",
            span: 24,
            required: true,
            options: yearList,
            validation: Yup.string().required("CANNOT_BE_EMPTY"),
        },
        {
            type: "text",
            key: "chassisNumber",
            label: "Chassis_number",
            span: 24,
            required: false,
            props: { maxLength: 17 },
            validation: Yup.string()
                .max(17, "CHASSIS_NUMBER_INVALID")
                .matches(/^[a-zA-Z0-9]*$/, "CHASSIS_NUMBER_INVALID"),
        },
        {
            type: "number",
            key: "downPayment",
            label: "Down_payment",
            span: 24,
            required: true,
            validation: Yup.number()
                .integer("MUST_BE_INTEGER")
                .test("is-less-than-20-percen", "DOWN_PAYMENT_20", function (value) {
                    const { assetPrice } = this.parent;
                    return value === 0 || (value >= 3000 && value < assetPrice * 0.2);
                })
                .required("DOWN_PAYMENT_REQUIRED"),
        },
        {
            type: "number",
            key: "ballonPayment",
            label: "Ballon_payment",
            span: 24,
            required: true,
            validation: Yup.number()
                .integer("MUST_BE_INTEGER")
                .test("is-less-than-20-percent", "BALLON_PAYMENT_40", function (value) {
                    const { assetPrice } = this.parent;
                    return value <= assetPrice * 0.4;
                })
                .required("BALLON_REQUIRED"),
        },
        {
            type: "select",
            key: "dealerId",
            label: "Dealer_name",
            span: 24,
            options: dealerList,
            required: true,
            validation: Yup.string().required("CANNOT_BE_EMPTY"),
        },
    ];

    const personalFinanceSchema = ({ hideRequestedLoanAmount = true, tenureOptions = [] }) => [
        {
            type: "number",
            key: "request_amount",
            label: "Requested_loan_amount",
            span: 24,
            required: !hideRequestedLoanAmount,
            disabled: hideRequestedLoanAmount,
            pattern: "^[0-9]*$",
            validation: Yup.number()
                .required("CANNOT_BE_EMPTY")
                .when([], (values, schema) => {
                    if (isSaudiUser) {
                        return schema.min(20000, "MUST_BE_ATLEAST_20000").max(500000, "CANNOT_EXCEED_500000");
                    } else {
                        return schema.min(5000, "MUST_BE_ATLEAST_5000").max(80000, "CANNOT_EXCEED_80000");
                    }
                })
        },
        {
            type: "select",
            key: "tenure",
            label: "Tenure",
            required: true,
            options: tenureOptions,
            // value: 4,
            span: 24,
            validation: Yup.string()
                .required("CANNOT_BE_EMPTY")
            // .when('request_amount', (request_amount, schema) => {
            //   if (request_amount >= 2500 && request_amount <= 5000) {
            //     return schema.test(
            //       'max1',
            //       'MAX_12_MONTHS',
            //       value => { return parseInt(value, 10) <= 4 }
            //     );
            //   } else if (request_amount >= 5001 && request_amount <= 20000) {
            //     return schema.test(
            //       'max2',
            //       'MAX_36_MONTHS',
            //       value => parseInt(value, 10) <= 8
            //     );
            //   } else if (request_amount >= 20001) {
            //     return schema.test(
            //       'max3',
            //       'MAX_60_MONTHS',
            //       value => parseInt(value, 10) <= 12
            //     );
            //   }
            //   return schema; // default case if none of the conditions are met
            // }),
        },
        // { type: "number", key: "IBAN", label: "IBAN", span: 20, translation: 'REQUEST_DETAILS.IBAN' },
        {
            type: "text",
            key: "IBAN",
            label: "IBAN",
            span: 24,
            addonBefore: "SA",
            props: {
                maxLength: 22,
            },
            required: !hideRequestedLoanAmount,
            disabled: hideRequestedLoanAmount,
            validation: Yup.string()
                .when(['productId', '$hideRequestedLoanAmount'], ([productId, hide], schema) => {
                    if (hideRequestedLoanAmount)
                        return schema.notRequired();


                    return schema.required("CANNOT_BE_EMPTY")
                        .length(22, "IBAN_22")
                        .matches(/^[A-Z0-9]+$/i, "IBAN_ALPHANUMERIC_ONLY");
                }
                )
        },
        {
            type: "select",
            key: "loanReason",
            label: "LOAN_REASON",
            span: 24,
            options: loanReasonsOptions,
            required: !hideRequestedLoanAmount,
            disabled: hideRequestedLoanAmount,
            validation: Yup.string()
                .when('productId', (productId, schema) => {
                    if (hideRequestedLoanAmount)
                        return schema.notRequired();

                    return schema.required("CANNOT_BE_EMPTY")
                })
        },

        {
            type: "number",
            key: "salesRef",
            label: "SalesRef",
            span: 24,
            required: false,
            max: 10,
            min: 8,
            maxLength: 10,
            minLength: 8,
            validation: Yup.string()
                .matches(/^\d{8,10}$/, "MUST_BE_BETWEEN_8_AND_10_DIGITS")
        },
    ];

    const microFinanceSchema = ({ hideRequestedLoanAmount = true, tenureOptions = [] }) => [
        {
            type: "number",
            key: "request_amount",
            label: "Requested_loan_amount",
            span: 24,
            required: !hideRequestedLoanAmount,
            disabled: hideRequestedLoanAmount,
            pattern: "^[0-9]*$",
            validation: Yup.number()
                .required("CANNOT_BE_EMPTY")
                .when([], (values, schema) => {
                    if (id === '3') { // Micro Finance
                        return schema.min(5000, "MUST_BE_ATLEAST_5000").max(20000, "CANNOT_EXCEED_20000");
                    }
                    // else {
                    //     return schema.min(5000, "MUST_BE_ATLEAST_5000").max(20000, "CANNOT_EXCEED_20000");
                    // }
                })
        },
        {
            type: "select",
            key: "tenure",
            label: "Tenure",
            required: true,
            options: tenureOptions,
            // value: 4,
            span: 24,
            validation: Yup.string()
                .required("CANNOT_BE_EMPTY")
            // .when('request_amount', (request_amount, schema) => {
            //   if (request_amount >= 2500 && request_amount <= 5000) {
            //     return schema.test(
            //       'max1',
            //       'MAX_12_MONTHS',
            //       value => { return parseInt(value, 10) <= 4 }
            //     );
            //   } else if (request_amount >= 5001 && request_amount <= 20000) {
            //     return schema.test(
            //       'max2',
            //       'MAX_36_MONTHS',
            //       value => parseInt(value, 10) <= 8
            //     );
            //   } else if (request_amount >= 20001) {
            //     return schema.test(
            //       'max3',
            //       'MAX_60_MONTHS',
            //       value => parseInt(value, 10) <= 12
            //     );
            //   }
            //   return schema; // default case if none of the conditions are met
            // }),
        },
        // { type: "number", key: "IBAN", label: "IBAN", span: 20, translation: 'REQUEST_DETAILS.IBAN' },
        {
            type: "text",
            key: "IBAN",
            label: "IBAN",
            span: 24,
            addonBefore: "SA",
            props: {
                maxLength: 22,
            },
            required: !hideRequestedLoanAmount,
            disabled: hideRequestedLoanAmount,
            validation: Yup.string()
                .when(['productId', '$hideRequestedLoanAmount'], ([productId, hide], schema) => {
                    if (hideRequestedLoanAmount)
                        return schema.notRequired();


                    return schema.required("CANNOT_BE_EMPTY")
                        .length(22, "IBAN_22")
                        .matches(/^[A-Z0-9]+$/i, "IBAN_ALPHANUMERIC_ONLY");
                }
                )
        },
        {
            type: "select",
            key: "loanReason",
            label: "LOAN_REASON",
            span: 24,
            options: loanReasonsOptions,
            required: !hideRequestedLoanAmount,
            disabled: hideRequestedLoanAmount,
            validation: Yup.string()
                .when('productId', (productId, schema) => {
                    if (hideRequestedLoanAmount)
                        return schema.notRequired();

                    return schema.required("CANNOT_BE_EMPTY")
                })
        },

        {
            type: "number",
            key: "salesRef",
            label: "SalesRef",
            span: 24,
            required: false,
            max: 10,
            min: 8,
            maxLength: 10,
            minLength: 8,
            validation: Yup.string()
                .matches(/^\d{8,10}$/, "MUST_BE_BETWEEN_8_AND_10_DIGITS")
        },
    ];

    const TenureUpdater = (props) => {
        // const { values, setFieldValue } = useFormikContext();
        // console.log(props?.values?.request_amount)

        // V2 Auto Lease: Populate independent list states from RTK Query data
        useEffect(() => {
            if (id === '1' || id === '4') {
                setYearList(generateYearArray(2022));

                if (manufacturersData) {
                    setManufacturerList(manufacturersData.map(({ ID, DESCRIPTION, DESCRIPTIONAR }) => ({
                        label: String(DESCRIPTION),
                        value: String(ID),
                        labelAr: String(DESCRIPTIONAR || DESCRIPTION),
                    })));
                }
                if (dealersData) {
                    // Filter dealers based on PRODUCT and SUBPRODUCT
                    const expectedProduct = 'SMB.TA.IJRAH.FINANCE';
                    const expectedSubProduct = id === '1' ? 'IJMB-AUTO' : id === '4' ? 'IJMB-MOTORBIKE' : '';
                    
                    const filteredDealers = dealersData.filter(dealer => 
                        dealer.PRODUCT === expectedProduct && dealer.SUBPRODUCT === expectedSubProduct
                    );
                    
                    setDealerList(filteredDealers.map(({ ID, DESCRIPTION, DESCRIPTIONAR }) => ({
                        label: String(DESCRIPTION),
                        value: String(ID),
                        labelAr: String(DESCRIPTIONAR || DESCRIPTION),
                    })));
                }
                if (colorsData) {
                    setColorList(colorsData.map(({ ID, DESCRIPTION, DESCRIPTIONAR }) => ({
                        label: String(DESCRIPTION),
                        value: String(ID),
                        labelAr: String(DESCRIPTIONAR || DESCRIPTION),
                    })));
                }
                if (assetConditionsData) {
                    setConditionList(assetConditionsData.map(({ ID, DESCRIPTION, DESCRIPTIONAR }) => ({
                        label: String(DESCRIPTION),
                        value: String(ID),
                        labelAr: String(DESCRIPTIONAR || DESCRIPTION),
                    })));
                }
            }
        }, [id, manufacturersData, dealersData, colorsData, assetConditionsData]);

        // Cascading: When manufacturer changes, fetch models for that make
        useEffect(() => {
            if ((id === '1' || id === '4') && props.values?.assetManufacturer) {
                triggerGetAssetModels({ assetMakeId: props.values.assetManufacturer });
                // Clear dependent fields
                setModelTypeList([]);
                props.setFieldValue('assetModel', '');
                props.setFieldValue('assetModelType', '');
            } else {
                setModelList([]);
                setModelTypeList([]);
            }
        }, [props.values?.assetManufacturer]);

        // Cascading: When model changes, fetch model types for that make + model
        useEffect(() => {
            if ((id === '1' || id === '4') && props.values?.assetManufacturer && props.values?.assetModel) {
                triggerGetAssetModelTypes({ assetMakeId: props.values.assetManufacturer, assetModelId: props.values.assetModel });
                // Clear dependent field
                props.setFieldValue('assetModelType', '');
            } else {
                setModelTypeList([]);
            }
        }, [props.values?.assetModel]);

        // Transform models data when it arrives
        useEffect(() => {
            if (assetModelsData) {
                setModelList(assetModelsData.map(({ ID, DESCRIPTION, DESCRIPTIONAR }) => ({
                    label: String(DESCRIPTION),
                    value: String(ID),
                    labelAr: String(DESCRIPTIONAR || DESCRIPTION),
                })));
            }
        }, [assetModelsData]);

        // Transform model types data when it arrives
        useEffect(() => {
            if (assetModelTypesData) {
                setModelTypeList(assetModelTypesData.map(({ ID, DESCRIPTION, DESCRIPTIONAR }) => ({
                    label: String(DESCRIPTION),
                    value: String(ID),
                    labelAr: String(DESCRIPTIONAR || DESCRIPTION),
                })));
            }
        }, [assetModelTypesData]);


        const tenureOpt = useMemo(() => {
            const amount = props.values?.request_amount;
            if (id === "2") { //if tawarruq then validation
                if (isSaudiUser) { //if user is Saudi
                    if (amount < 20000 || amount > 500000) return [];
                    if (amount >= 20000 && amount <= 500000) return [
                        { value: '6', label: '6' },
                        { value: '12', label: '12' },
                        { value: '18', label: '18' },
                        { value: '24', label: '24' },
                        { value: '30', label: '30' },
                        { value: '36', label: '36' },
                        { value: '42', label: '42' },
                        { value: '48', label: '48' },
                        { value: '54', label: '54' },
                        { value: '60', label: '60' },
                    ];
                }
                if (!isSaudiUser) { //if user is non-Saudi
                    if (amount < 5000 || amount > 80000) return [];
                    // if (amount <= 3400) return [{ value: '6', label: '6' }];
                    // if (amount >= 3401 && amount <= 9999) return [{ value: '6', label: '6' }, { value: '12', label: '12' }];
                    // if (amount >= 10000 && amount <= 25000) return [
                    //     { value: '6', label: '6' },
                    //     { value: '12', label: '12' },
                    //     { value: '24', label: '24' },
                    //     { value: '36', label: '36' }
                    // ];
                    // if (amount >= 25001) return [
                    //     { value: '6', label: '6' },
                    //     { value: '12', label: '12' },
                    //     { value: '24', label: '24' },
                    //     { value: '36', label: '36' },
                    //     { value: '48', label: '48' },
                    //     { value: '60', label: '60' }
                    // ];
                    if (amount >= 5000 && amount <= 80000) return [
                        { value: "6", label: "6" },
                        { value: "12", label: "12" },
                        { value: "18", label: "18" },
                        { value: "24", label: "24" },
                        { value: "30", label: "30" },
                        { value: "36", label: "36" }];
                }
            }
            if (id === "3") { //if microfinance and saudi then validation
                // if (isSaudiUser) {
                if (amount < 5000 || amount > 20000) return [];
                if (amount >= 5000 && amount <= 20000) return [
                    { value: '6', label: '6' },
                    { value: '12', label: '12' },
                    { value: '18', label: '18' },
                    { value: '24', label: '24' }
                ];
                // }
            }
            else {
                return [
                    { value: "6", label: "6" },
                    { value: "12", label: "12" },
                    { value: "24", label: "24" },
                    { value: "36", label: "36" },
                    { value: "48", label: "48" },
                    { value: "60", label: "60" },
                ]
            }
            return [];
        }, [props.values?.request_amount]);

        let tenureSelectionCheck = tenureOpt.find(opt => opt.value === props.values?.tenure)
        if (tenureSelectionCheck === undefined && props.values?.tenure !== "") {
            props.setValues((prev) => ({
                ...prev,
                tenure: ""
            }))
        }

        setTenureOptions(tenureOpt);

        return null; // This component doesn't render anything directly, it is only for computational purposes only
    };

    // V1 Submit function - needs all params
    const productAppSubmit = async (paramValues, appId, idbUserId) => {
        {
            const values = {
                ...filterObjectByKeys(
                    {
                        ...paramValues,
                        IBAN: paramValues.IBAN ? "SA" + paramValues?.IBAN : null,
                        ...(assetId && (id == '1' || id == '4') ? { assetId: assetId } : {}), //assetId is not null and available
                    },
                    APP_DETAILS_DTO_KEYS
                ),
                productId: (id === "1" || id === "4") ? '217' : '328',  //ijarah or tawarruq
                subproductId: id === "1" ? '250' : id === '2' ? '329' : id == '3' ? '374' : '361', //auto, commodity, micro or moto
                applStatus: 'S' //submitted
            };


            const response = await updateApplicationDetails({
                appId: appId,
                userWebId: idbUserId,
                source: 'WEB',
                details: values,
            });
            if (response.error) {
                console.log("Error in update application", response.error);
                toast.error("Error in creating application");
                setIsCreating(false); // Reset creating state on error
                return
            }
            if (response.data) {
                toast.success("Application Created Successfully");
                setIsCreating(false); // Reset creating state on success
                navigate(`/${routes.APP_ROOT}/${routes.CREATE_REQUEST}/${routes.CREATE_REQUEST_SUCCESS}/${appId}/1`, { replace: true });
            }
        }
    }

    // V2 Submit function - only needs applicationId
    const productAppSubmitV2 = async (applicationId) => {
        console.log('v2 submit application with applicationId:', applicationId);
        try {
            const response = await updateApplicationDetailsV2({
                applicationId: applicationId
            });

            if (response.error) {
                console.log("Error in V2 submit application", response.error);
                toast.error("Error in submitting application");
                setIsCreating(false);
                return;
            }

            if (response.data) {
                toast.success("Application Submitted Successfully");
                setIsCreating(false);
                navigate(`/${routes.APP_ROOT}/${routes.CREATE_REQUEST}/${routes.CREATE_REQUEST_SUCCESS}/${applicationId}/2`, { replace: true });
            }
        } catch (error) {
            console.error("Error in V2 productAppSubmit:", error);
            toast.error("Error in submitting application");
            setIsCreating(false);
        }
    };

    // V2 Document upload function
    const uploadV2Documents = async (appId) => {
        const documentsToUpload = [
            { file: selectedBankStatement, type: 'BankStatements' },
            { file: selectedPaySlip, type: 'Payslips' },
            { file: selectedOtherDocuments, type: 'OtherDocuments' }
        ].filter(doc => doc.file !== null);

        if (documentsToUpload.length === 0) {
            return true; // No documents to upload
        }

        let allSuccess = true;

        for (const doc of documentsToUpload) {
            try {
                // Set appropriate uploading state
                if (doc.type === 'BankStatements') setBankStatementUploading(true);
                else if (doc.type === 'Payslips') setPaySlipUploading(true);
                else if (doc.type === 'OtherDocuments') setOtherDocumentsUploading(true);

                const success = await uploadSingleDocument(doc.file, doc.type, appId);
                if (!success) {
                    allSuccess = false;
                    // break;
                }
            } catch (error) {
                console.error(`Error uploading ${doc.type}:`, error);
                allSuccess = false;
                // break;
            } finally {
                // Reset uploading state
                if (doc.type === 'BankStatements') setBankStatementUploading(false);
                else if (doc.type === 'Payslips') setPaySlipUploading(false);
                else if (doc.type === 'OtherDocuments') setOtherDocumentsUploading(false);
            }
        }

        return allSuccess;
    };

    // Helper function to upload a single document
    const uploadSingleDocument = async (file, documentType, appId) => {
        const documentConfig = {
            'BankStatements': {
                evidenceType: "BankStatements",
                forRequirements: "SupportingFinancialDocuments-BankStatements"
            },
            'Payslips': {
                evidenceType: "Payslips",
                forRequirements: "ProofOfIncome-Payslips"
            },
            'OtherDocuments': {
                evidenceType: "OtherDocuments",
                forRequirements: "SupportingFinancialDocuments-OtherDocuments"
            }
        };

        const config = documentConfig[documentType];
        if (!config) return false;

        try {
            const formData = new FormData();
            formData.append("document", file);
            formData.append("appId", appId);
            formData.append("evidenceType", config.evidenceType);
            formData.append("forRequirements", config.forRequirements);

            const data = JSON.parse(localStorage.getItem("@@TAMWEEL_USER"));

            await axios.post(`${env.API_ENDPOINT_V2}app/upload-document`, formData, {
                headers: {
                    Authorization: `Bearer ${data.token}`,
                },
            });
            // await axios.post(`${getApiEndpoint()}app/upload-document`, formData, {
            //     headers: {
            //         Authorization: `Bearer ${data.token}`,
            //     },
            // });

            return true;
        } catch (error) {
            console.error(`Failed to upload ${documentType}:`, error);
            toast.error(`Failed to upload ${documentType}.`);
            return false;
        }
    };

    const ProductAppCreate = async (values) => {
        let createAppPayload = {};
        try {
            setIsCreating(true); // Set creating state to true at the beginning
            const user = getUser();

            if (!user) {
                setIsCreating(false);
                return;
            }

            if (values?.IBAN) { //iban verification first before creating app
                const data = JSON.parse(localStorage.getItem("@@TAMWEEL_USER"));
                try {
                    setIbanVerifyLoader(true)
                    const verificationResult = await fetch(`${env.API_ENDPOINT}app/iban_verification`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${data.token}` },
                        body: JSON.stringify(
                            {
                                iban: `SA${values?.IBAN}`,
                                nationalId: data.USER_ID
                            }
                        )
                    })
                    if (!verificationResult.ok) {
                        const errorResult = await verificationResult.json()
                        if (errorResult)
                            setIbanVerifyLoader(false)
                        switch (verificationResult.status) {
                            case 400:
                                toast.error(`IBAN verification failed: ${errorResult.error?.message}`)
                                break;
                            case 429:
                                toast.error(`IBAN verification failed: ${errorResult.error?.message}`)
                                break;
                            case 500:
                                toast.error(`IBAN verification failed: ${errorResult.error?.message}`)
                                break;
                            default:
                                break;
                        }
                        setIsCreating(false); // Reset creating state on error
                        return
                    }
                    const response = await verificationResult.json()
                    if (response?.iban_verification && !response?.error?.message) {
                        setIbanVerifyLoader(false)
                        toast.success(translate("VALIDATION.IBAN_VAL_SUCCESS"))
                    }
                    else {
                        setIbanVerifyLoader(false)
                        toast.error(translate("VALIDATION.IBAN_VAL_FAILED"))
                        setIsCreating(false); // Reset creating state on error
                        return
                    }
                } catch (error) {
                    setIbanVerifyLoader(false)
                    toast.error(translate("VALIDATION.IBAN_VAL_FAILED"))
                    setIsCreating(false); // Reset creating state on error
                    return
                }
            }

            // V1 vs V2 Flow Logic
            if (userHasAccessV2 && (id == '1' || id == '4')) {
                // V2 Flow: Ijarah (Auto Lease & Moto)
                createAppPayload = {
                    productRef: "SMB.TA.IJRAH.FINANCE",
                    productGroupRef: "SMB.TA.IJRAH.FINANCE",
                    productName: "IJMB",
                    productSubType: id == '1' ? "IJMB-AUTO" : "IJMB-MOTORBIKE",
                    idNumber: String(user?.USER_ID),
                    idType: String(user?.USER_ID)[0] === "1" ? "NATIONAL.ID" : "IQAMA.ID",
                    source: "WEB",
                    userWebId: String(user?.IDB_USR_ID),
                    details: {
                        tenure: values.tenure ? Number(values.tenure) : undefined,
                        assetMake: String(values.assetManufacturer) || undefined,
                        assetModel: String(values.assetModel) || undefined,
                        assetModelType: String(values.assetModelType) || undefined,
                        assetCondition: String(values.assetCondition) || undefined,
                        colorId: String(values.colorId) || undefined,
                        assetPrice: values.assetPrice ? String(values.assetPrice) : undefined,
                        assetModelYear: values.assetModelYear ? String(values.assetModelYear) : undefined,
                        chassisNumber: values.chassisNumber ? String(values.chassisNumber) : undefined,
                        downPayment: values.downPayment ? Number(values.downPayment) : undefined,
                        ballonPayment: values.ballonPayment ? Number(values.ballonPayment) : undefined,
                        dealerId: values.dealerId || undefined,
                        salesRef: values.salesRef ? String(values.salesRef) : undefined,
                    },
                };

                const res = await createNewIjarahApp(createAppPayload).unwrap();

                if (res?.data?.applicationId) {
                    const appId = res.data.applicationId;
                    setCurrentAppId(appId);

                    // V2 Flow: Upload documents first, then show IVR modal
                    const documentsUploaded = await uploadV2Documents(appId);

                    if (!documentsUploaded) {
                        toast.error("Error uploading documents");
                    }

                    // Show IVR verification modal instead of submitting
                    setIvrApplicationId(appId);
                    setIsIvrModalVisible(true);
                    setIsCreating(false);
                } else {
                    console.error("No applicationId received from Ijarah V2 API");
                    setIsCreating(false);
                }
            } else if (userHasAccessV2 && (id == '2' || id == '3')) {
                // V2 Flow: For Tawaruq personal finance and micro finance
                createAppPayload = {
                    productRef: id == '2' ? "SMB.TA.PERSONAL.FINANCE" : "SMB.TA.PERSONAL.FINANCE", //ternary for future if incase it changes
                    productGroupRef: "SMB.TA.PERSONAL.FINANCE",
                    productName: "APL",
                    productSubType: id == '2' ? "COMM" : "MICROFINANCE",
                    idNumber: String(user?.USER_ID),
                    idType: String(user?.USER_ID)[0] === "1" ? "NATIONAL.ID" : "IQAMA.ID",
                    source: "WEB",
                    userWebId: String(user?.IDB_USR_ID),
                    details: {
                        ...values,
                        IBAN: values.IBAN ? `SA${values.IBAN}` : values.IBAN
                    },
                };

                const res = await createNewTawaruqApp(createAppPayload).unwrap();

                if (res?.data?.applicationId) {
                    const appId = res.data.applicationId;
                    setCurrentAppId(appId);

                    // V2 Flow: Upload documents first, then show IVR modal
                    const documentsUploaded = await uploadV2Documents(appId);

                    if (!documentsUploaded) {
                        toast.error("Error uploading documents");
                        // setIsCreating(false);
                    }

                    // Show IVR verification modal instead of submitting
                    setIvrApplicationId(appId);
                    setIsIvrModalVisible(true);
                    setIsCreating(false);
                } else {
                    console.error("No applicationId received from V2 API");
                    setIsCreating(false);
                }
            } else {
                // V1 Flow: For all other cases (Auto and Microfinance)
                createAppPayload = {
                    id: String(user?.IDB_USR_ID),
                    idNumber: String(user?.USER_ID),
                    idType: String(user?.USER_ID)[0] === "1" ? "NID" : "Q",
                };

                const res = await createNewApp(createAppPayload).unwrap();

                if (res?.query) {
                    const appId = res.query;
                    setCurrentAppId(appId);

                    // Handle documents if they exist
                    if (fileList && fileList.length > 0) {
                        handleUploadDocuments(fileList, appId);
                    }

                    // V1 productAppSubmit - needs full values and form data
                    await productAppSubmit(values, appId, String(user?.IDB_USR_ID));
                } else {
                    console.error("No query received from V1 API");
                    setIsCreating(false);
                }
            }
        } catch (ex) {
            console.log("Error in create application", ex);
            const errorCode = ex?.data?.error?.code;
            if (errorCode === 'ACTIVE_APPLICATION' || errorCode === 'APPLICATION_EXISTS') {
                toast.error(translate('ACTIVE_APPLICATION'));
            } else {
                toast.error(ex?.data?.error?.message || "Error in creating application");
            }
            setIsCreating(false); // Reset creating state on error
        }
    }

    const handleUploadDocuments = async (files, appId) => {
        setIsUploading(true);
        let updatedDocumentDetails = [...files];
        let allSuccess = true;
        try {
            await Promise.all(
                fileList.map(async (file, index) => {
                    const formData = new FormData();
                    formData.append("document", file);
                    formData.append("appId", appId);
                    const data = JSON.parse(localStorage.getItem("@@TAMWEEL_USER"));

                    try {
                        //hardcoded values for V1 - need to be dynamic in future
                        await axios.post(`${env.API_ENDPOINT}app/upload-document`, formData, {
                            headers: {
                                Authorization: `Bearer ${data.token}`,
                            },
                        });
                        // Remove the file from the state after successful upload
                        updatedDocumentDetails.splice(index, 1);
                    } catch (err) {
                        allSuccess = false;
                        toast.error("Failed to upload file.");
                    }
                })
            );
        } catch (error) {
            allSuccess = false;
            console.error("Document upload error:", error);
            toast.error("Failed to upload documents.");
            setIsCreating(false); // Reset creating state if the overall upload fails
        } finally {
            setIsUploading(false);
        }
        // if (!allSuccess) {
        //     setIsCreating(false); // Uncomment this if you want to stop the process when any file upload fails
        // }
        // if (allSuccess) {
        //     toast.success("Application Created Successfully");
        //     navigate(appRoute(routes.HOME));
        // }
    }

    const beforeUpload = (file) => {
        const isJpgOrPngOrPdf =
            file.type === "image/jpeg" ||
            file.type === "image/png" ||
            file.type === "image/jpg" ||
            file.type === "application/pdf";
        if (!isJpgOrPngOrPdf) {
            toast.error(translate("SETTINGS.ERROR_FILE_TYPE"));
            return true;
        }

        const isLt3M = file.size / 1024 / 1024 < 3;
        if (!isLt3M) {
            toast.error(translate("SETTINGS.ERROR_FILE_SIZE_3MB"));
            return true;
        }

        const fileName = file.name;
        const multipleExtensionsPattern = /(\.[^.\s]{2,4}){2,}$/;
        if (multipleExtensionsPattern.test(fileName)) {
            toast.error(translate("SETTINGS.ERROR_FILE_MULTIPLE_EXT"));
            return true;
        }
        const invalidFileNamePattern = /^[^.]+\.[^.]+\..+\.[^.]+$/;

        if (invalidFileNamePattern.test(fileName)) {
            toast.error(translate("SETTINGS.ERROR_FILE_MULTIPLE_EXT"));
            return true;
        }

        if (fileName.toLowerCase().includes('contract')) {
            toast.error(translate("SETTINGS.ERROR_FILE_NAME_CONTRACT"));
            return true
        }
        setUploadFile(file)
        return false;
    };

    const validateFile = (file) => {
        const fileName = file.name;

        // Check for duplicate filename first (for V2 users)
        if (userHasAccessV2 && isDuplicateFileName(fileName)) {
            const errorMessage = language.code === 'ar'
                ? `ملف بنفس الاسم "${fileName}" موجود بالفعل. الرجاء إعادة تسمية الملف أو اختيار ملف آخر.`
                : `A file with the name "${fileName}" already exists. Please rename the file or choose a different one.`;

            toast.error(errorMessage, {
                position: "top-center",
                autoClose: 5000,
            });
            return false;
        }

        const isValidType =
            // file.type === "image/jpeg" ||
            // file.type === "image/png" ||
            // file.type === "image/jpg" ||
            file.type === "application/pdf";

        if (!isValidType) {
            // toast.error("Please upload only PDF, JPG, JPEG, or PNG files");
            toast.error("Please upload only PDF files");
            return false;
        }

        const isUnder3MB = file.size / 1024 / 1024 < 3;
        if (!isUnder3MB) {
            toast.error("File size must be less than 3MB");
            return false;
        }

        // const fileName = file.name;
        const multipleExtensionsPattern = /(\.[^.\s]{2,4}){2,}$/;
        if (multipleExtensionsPattern.test(fileName)) {
            toast.error("File name cannot have multiple extensions");
            return false;
        }

        const invalidFileNamePattern = /^[^.]+\.[^.]+\..+\.[^.]+$/;
        if (invalidFileNamePattern.test(fileName)) {
            toast.error("File name cannot have multiple extensions");
            return false;
        }

        if (fileName.toLowerCase().includes("contract")) {
            toast.error("Files containing 'contract' in the name are not allowed");
            return false;
        }

        return true; // File is valid
    };

    // V2 Document selection component (before app creation)
    const V2DocumentSelectionComponent = ({ title, file, onSelect, onRemove }) => {
        const handleFileSelect = (info) => {
            const selectedFile = info.file;
            if (validateFile(selectedFile)) {
                onSelect(selectedFile);
            }
        };

        const uploadProps = {
            name: 'file',
            multiple: false,
            accept: "application/pdf",
            beforeUpload: () => false, // Prevent automatic upload
            onChange: handleFileSelect,
            showUploadList: false
        };

        return (
            <div className="border border-gray-200 border-dashed rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                            <UploadOutlined className="text-teal-600" />
                        </div>
                        <div>
                            <p className="font-medium text-gray-700">{translate(`CREATE_REQUEST.${title}`)}</p>
                            {file && (
                                <p className="text-sm text-gray-500">{file.name}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {file ? (
                            <Button
                                onClick={onRemove}
                                className="text-red-500 border-red-500 hover:bg-red-50"
                                size="small"
                                icon={<DeleteOutlined />}
                            >
                                {translate("CREATE_REQUEST.REMOVE")}
                            </Button>
                        ) : (
                            <Upload {...uploadProps}>
                                <Button
                                    type="primary"
                                    className="bg-primary border-primary hover:bg-primary-dark"
                                >
                                    {translate("CREATE_REQUEST.SELECT_FILE")}
                                </Button>
                            </Upload>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const dragAndDropProps = {
        name: 'file',
        multiple: true,
        maxCount: 3,
        accept: "image/png,image/jpeg,image/jpg,application/pdf",
        beforeUpload,
        fileList,
        onChange(info) {
            if (info.file.status === 'error' || info.file.status === 'removed') {
                setFileList(prev => prev.filter(i => i.uid !== info.file.uid))
                return
            }
            const isValid = validateFile(info.file);
            if (isValid) {
                setFileList([...fileList, info.file])
                // dispatch({ type: PATCH_DOCUMENT_DETAILS, payload: [...fileList, info.file] });
            }
        },
        onDrop(e) {
            setFileList([e.dataTransfer.files])
        },
    };

    const handleRemove = (e) => {
        let updateFiles = fileList.filter(i => i.uid !== e.uid)
        // console.log('hi', updateFiles)
        // dispatch({ type: PATCH_DOCUMENT_DETAILS, payload: updateFiles });
        setFileList(prev => prev.filter(i => i.uid !== e.uid));
        setUploadFile(null)
    }

    const schemaFields = useMemo(
        () => {
            let hideRequestedLoanAmount = false;

            if (id === '1') { //ijarah
                hideRequestedLoanAmount = true;
                return autoLeaseSchema({
                    hideRequestedLoanAmount: hideRequestedLoanAmount,
                    tenureOptions,
                    manufacturerList,
                    modelList,
                    modelTypeList,
                    dealerList,
                    colorList,
                    conditionList,
                    yearList,
                })

            }
            else if (id === '2') { //tawarruq
                return personalFinanceSchema({
                    hideRequestedLoanAmount: hideRequestedLoanAmount,
                    tenureOptions,
                })
            }
            else if (id === '3') {
                return microFinanceSchema({
                    hideRequestedLoanAmount: hideRequestedLoanAmount,
                    tenureOptions,
                })
            }
            else if (id === '4') {  //moto finance has the same schema as auto leasing
                hideRequestedLoanAmount = true;
                return autoLeaseSchema({
                    hideRequestedLoanAmount: hideRequestedLoanAmount,
                    tenureOptions,
                    manufacturerList,
                    modelList,
                    modelTypeList,
                    dealerList,
                    colorList,
                    conditionList,
                    yearList,
                })

            }
        },
        [tenureOptions, manufacturerList, modelList, modelTypeList, dealerList, colorList, conditionList, yearList, loanReasonsOptions]
    );

    const ProductCards = (product) => {
        return (
            <div
                className="w-full max-w-[450px] max-h[250px] overflow-hidden"
            >
                <div key={product.id} className="bg-[#E6F1F2] rounded-lg overflow-hidden flex flex-row">
                    <div className="p-6 flex flex-col justify-between flex-1">
                        <div>
                            <h3 className="text-[#1F225B] text-xl md:text-2xl font-light mb-2">{product.title}</h3>
                            <p className="text-[#8E9196] text-sm md:text-base mb-4">{translate(product.description)}</p>
                        </div>
                    </div>
                    <div className="md:w-2/5 sm:w-2/5 w-1/2">
                        <img src={product.image} alt={product.id} className="w-full h-full object-cover" />
                    </div>
                </div>

            </div>
        )
    }

    const autoLeasingCardDetails = (product) => {
        return (
            <div
                bordered={false}
                className="bg-transparent shadow-none rounded-none border-none outline-none"
                style={{
                    width: '100%',
                    maxWidth: "550px",
                    minHeight: "250px", boxShadow: "none", // Remove shadow
                    border: "none",    // Remove border
                    borderRadius: "0", // Remove border-radius
                    outline: "none",
                    padding: "0"
                    // direction: 'ltr'

                }}
            >
                <p className="text-[#878787] mt-3 text-base md:text-lg font-light text-start mb-2">{translate(product.subDescription)}</p>
                <ul className="mt-3">
                    {[
                        translate('PRODUCTS.AUTO_PT1'),
                        translate('PRODUCTS.AUTO_PT2'),
                        // <li className="flex items-center" key="riyal-price"> {/* Added flex and items-center to this li */}
                        //     {isArabic ?
                        //         <span className="break-words">
                        //             <img src={SaudiRiyal} alt="riyal" className="w-3 h-4 inline-block" /> {translate("VOUCHERS.STARTS_FROM")} {translate('VOUCHERS.FIVE_THOUSAND')}
                        //         </span>
                        //         :
                        //         <span className="break-words">
                        //             {translate("VOUCHERS.STARTS_FROM")} <img src={SaudiRiyal} alt="riyal" className="w-3 h-4 inline-block" /> {translate('VOUCHERS.FIVE_THOUSAND')}
                        //         </span>}
                        // </li>,
                        translate("PRODUCTS.AUTO_PT3"),
                        translate("PRODUCTS.AUTO_PT4"),
                        translate("PRODUCTS.AUTO_PT5"),
                        translate("PRODUCTS.AUTO_PT6"),
                        translate("PRODUCTS.AUTO_PT7"),
                        translate("PRODUCTS.AUTO_PT8"),
                    ].map((text, index) => (
                        <li className="flex mb-4 gap-2" key={index}>
                            <span className="flex-shrink-0">
                                <img src={Arrow} alt="checkmark" className="w-6 h-6" />
                            </span>
                            <span className="text-sm break-words">{text}</span>
                        </li>
                    ))}
                </ul>
                {/* <Button onClick={() => handleClick('1')} className="mt-4 bg-secondary border-none text-black font-semibold hover:!text-white hover:!bg-primary">
                    Select
                </Button> */}
                {/* <div className="mt-4">
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            className="form-checkbox border border-gray-400 w-10"
                            checked={termsChecked}
                            onChange={(e) => setTermsChecked(e.target.checked)}
                        />
                        <span className="text-gray-700 text-xs">{translate("TERMS_AND_CONDITIONS")}</span>
                    </label>
                </div> */}
            </div >
        )
    }

    const personalFinanaceCardDetails = (product) => {
        return (
            <div
                bordered={false}
                className="bg-transparent shadow-none rounded-none border-none outline-none p-0"
                style={{
                    width: '100%',
                    maxWidth: "550px",
                    minHeight: "250px", boxShadow: "none", // Remove shadow
                    border: "none",    // Remove border
                    borderRadius: "0", // Remove border-radius
                    outline: "none",
                    padding: "0"
                    // direction:'ltr'
                }}
            >
                <p className="text-[#878787] mt-3 text-base md:text-lg font-light text-start mb-3">{translate(product.subDescription)}</p>
                <ul className="mt-4">
                    {[
                        translate('PRODUCTS.PRSNL_PT1'),
                        translate('PRODUCTS.PRSNL_PT2'),
                        // <li className="flex items-center" key="riyal-price"> {/* Added flex and items-center to this li */}
                        //     {isArabic ?
                        //         <span className="break-words">
                        //             <img src={SaudiRiyal} alt="riyal" className="w-3 h-4 inline-block" /> {translate("VOUCHERS.STARTS_FROM")} {translate('VOUCHERS.FIVE_THOUSAND')}
                        //         </span>
                        //         :
                        //         <span className="break-words">
                        //             {translate("VOUCHERS.STARTS_FROM")} <img src={SaudiRiyal} alt="riyal" className="w-3 h-4 inline-block" /> {translate('VOUCHERS.FIVE_THOUSAND')}
                        //         </span>}
                        // </li>,
                        translate("PRODUCTS.PRSNL_PT3"),
                        translate("PRODUCTS.PRSNL_PT4"),
                        translate("PRODUCTS.PRSNL_PT5"),
                        translate("PRODUCTS.PRSNL_PT6"),
                        translate("PRODUCTS.PRSNL_PT7"),
                        // translate("PRODUCTS.AUTO_PT8"),
                        // <li className="flex items-center" key="riyal-price"> {/* Added flex and items-center to this li */}
                        //     {isArabic ?
                        //         <span className="break-words">
                        //             {translate("VOUCHERS.MAX_LIMIT")} {translate('VOUCHERS.FIVE_HUNDRED_THOUSAND')} <img src={SaudiRiyal} alt="riyal" className="w-3 h-4 inline-block" />
                        //         </span> :
                        //         <span className="break-words">
                        //             {translate("VOUCHERS.MAX_LIMIT")} <img src={SaudiRiyal} alt="riyal" className="w-3 h-4 inline-block" /> {translate('VOUCHERS.FIVE_HUNDRED_THOUSAND')}
                        //         </span>
                        //     }
                        // </li>,
                        // translate('VOUCHERS.SHERI_PT2'),
                    ].map((text, index) => (
                        <li className="flex gap-2 mb-4" key={index}>
                            <span className="flex-shrink-0">
                                <img src={Arrow} alt="checkmark" className="w-6 h-6" />
                            </span>
                            <span className="text-sm break-words">{text}</span>
                        </li>
                    ))}
                </ul>
                {/* <Button onClick={() => handleClick('2')} className="mt-4 bg-secondary border-none text-black font-semibold hover:!text-white hover:!bg-primary">
                    Select
                </Button> */}
                {/* <div className="mt-6">
                    <label className="flex items-center gap-2 mt-2">
                        <input
                            type="checkbox"
                            className="form-checkbox border border-gray-400 w-5 h-5 rounded-full"
                            checked={termsChecked}
                            onChange={(e) => setTermsChecked(e.target.checked)}
                        />
                        <span className="text-gray-700 text-sm">{translate("TERMS_AND_CONDITIONS")}</span>
                    </label>
                </div> */}
            </div>
        )
    }

    const microFinanceCardDetails = (product) => {
        return (
            <div
                bordered={false}
                className="bg-transparent shadow-none rounded-none border-none outline-none p-0"
                style={{
                    width: '100%',
                    maxWidth: "550px",
                    minHeight: "250px", boxShadow: "none", // Remove shadow
                    border: "none",    // Remove border
                    borderRadius: "0", // Remove border-radius
                    outline: "none",
                    padding: "0"
                }}
            >
                <p className="text-[#878787] mt-3 text-base md:text-lg font-light text-start mb-3">{translate(product.subDescription)}</p>
                <ul className="mt-4">
                    {[
                        translate('PRODUCTS.MICRO_PT1'),
                        translate('PRODUCTS.MICRO_PT2'),
                        translate("PRODUCTS.MICRO_PT3"),
                        translate("PRODUCTS.MICRO_PT4"),
                        translate("PRODUCTS.MICRO_PT5"),
                    ].filter(text => text).map((text, index) => (
                        <li className="flex gap-2 mb-4" key={index}>
                            <span className="flex-shrink-0">
                                <img src={Arrow} alt="checkmark" className="w-6 h-6" />
                            </span>
                            <span className="text-sm break-words">{text}</span>
                        </li>
                    ))}
                </ul>
            </div>
        )
    }


    const ProductCardSelector = {
        '1': ProductCards(autoLeaseDetails),
        '2': ProductCards(personalFinanceDetails),
        '3': ProductCards(microFinanceDetails),
        '4': ProductCards(motoFinanceDetails)
    }

    const ProductDetailsSelector = {
        '1': autoLeasingCardDetails(autoLeaseDetails),
        '2': personalFinanaceCardDetails(personalFinanceDetails),
        '3': microFinanceCardDetails(microFinanceDetails),
        '4': autoLeasingCardDetails(motoFinanceDetails)
    }

    return (
        <div className="rounded-xl mb-4 px-4 py-4 bg-white shadow-lg">
            <SalaryEligibilityGate productCode={eligibilityProductCode} />
            <div className="p-5">
                {/* <Button onClick={() => navigate(-1)}>{translate("Back")}</Button> */}
                <BackButton onClick={() => navigate(-1)} />

                <Row gutter={[16, 16]} className="pt-4">
                    {/* Card Section */}
                    <Col xs={24} md={10}>
                        {/* selecting content based on valid id */}
                        {id && ProductCardSelector[id]}
                        {id && ProductDetailsSelector[id]}
                    </Col>

                    {/* Form Section */}
                    <Col xs={24} md={14}>
                        <Formik
                            innerRef={formikRef}
                            enableReinitialize={true}
                            validateOnChange={true}
                            validateOnMount={true}
                            initialValues={buildInitialValues(schemaFields)}
                            validationSchema={buildValidationSchema(schemaFields)}
                            onSubmit={(values) => ProductAppCreate(values)}
                        >
                            {(props) => (
                                <Form onFinish={() => props.handleSubmit()}>
                                    {TenureUpdater(props)}
                                    <Title level={5} style={{ color: '#030438' }}>
                                        {translate('VOUCHERS.FILL_FORM')}
                                    </Title>
                                    <div>
                                        <FormDrawer schema={{ fields: schemaFields }} />
                                    </div>
                                    <div className="my-4">
                                        <label className="block text-gray-600 font-medium mb-2">
                                            {translate("SETTINGS.DOCUMENT")} {userHasAccessV2 && translate("SETTINGS.DOCUMENT_DES_V2")}
                                        </label>

                                        {/* V2 Users - Document Selection (before app creation) */}
                                        {userHasAccessV2 && (
                                            <>
                                                <V2DocumentSelectionComponent
                                                    title="BANK_STATEMENT"
                                                    file={selectedBankStatement}
                                                    onSelect={setSelectedBankStatement}
                                                    onRemove={() => setSelectedBankStatement(null)}
                                                />

                                                <V2DocumentSelectionComponent
                                                    title="PAY_SLIP"
                                                    file={selectedPaySlip}
                                                    onSelect={setSelectedPaySlip}
                                                    onRemove={() => setSelectedPaySlip(null)}
                                                />

                                                <V2DocumentSelectionComponent
                                                    title="OTHER_DOCUMENTS"
                                                    file={selectedOtherDocuments}
                                                    onSelect={setSelectedOtherDocuments}
                                                    onRemove={() => setSelectedOtherDocuments(null)}
                                                />
                                            </>
                                        )}
                                    </div>
                                    {/* <div className="mt-6">
                                        <label className="flex items-center gap-2 mt-2">
                                            <input
                                                type="checkbox"
                                                className="form-checkbox border border-gray-400 w-5 h-5 rounded-full accent-primary"
                                                checked={termsChecked}
                                                onChange={(e) => setTermsChecked(e.target.checked)}
                                            />
                                            <Link onClick={showTermsModal} className="text-blue-950 text-sm underline">{translate("TERMS_AND_CONDITIONS")}</Link>
                                        </label>
                                    </div> */}
                                    <div className="flex justify-end mt-8">
                                        <Button
                                            disabled={!props.isValid || isCreating || isLoading || isAnyFileUploading}
                                            type="primary"
                                            htmlType="submit"
                                            className="mt-4 bg-primary border-none text-white font-semibold p-6 w-40"
                                        >
                                            {(isLoading || isAnyFileUploading || isCreating) ? <LoadingOutlined /> :
                                                translate("APPLY")}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Col>
                </Row>
            </div>

            {/* IVR Verification Modal */}
            <Modal
                open={isIvrModalVisible}
                footer={null}
                closable={true}
                closeIcon={<CloseOutlined style={{ fontSize: '18px', color: '#000' }} />}
                onCancel={() => {
                    setIsIvrModalVisible(false);
                    // Navigate to application rating after closing modal
                    if (ivrApplicationId) {
                        navigate(`/${routes.APP_ROOT}/${routes.CREATE_REQUEST}/${routes.CREATE_REQUEST_SUCCESS}/${ivrApplicationId}/2`, { replace: true });
                    }
                }}
                centered
                width={500}
                bodyStyle={{ padding: '40px' }}
            >
                <div className="flex flex-col items-center text-center">
                    <img src={ivrPending} alt="IVR Verification" className="w-64 h-64 mb-6" />
                    <h2 className="text-2xl font-semibold mb-4 text-gray-800">
                        {translate("CREATE_REQUEST.IVR_VERIFICATION_TITLE")}
                    </h2>
                    <p className="text-gray-600 text-base leading-relaxed">
                        {translate("CREATE_REQUEST.IVR_VERIFICATION_MESSAGE")}
                    </p>
                    <Button
                        type="primary"
                        className="mt-6 bg-primary border-none text-white font-semibold px-16 py-5 rounded-3xl hover:bg-secondary"
                        onClick={() => {
                            setIsIvrModalVisible(false);
                            // Navigate to success page when button is clicked
                            if (ivrApplicationId) {
                                navigate(`/${routes.APP_ROOT}/${routes.CREATE_REQUEST}/${routes.CREATE_REQUEST_SUCCESS}/${ivrApplicationId}/2`, { replace: true });
                            }
                        }}
                    >
                        {translate("CREATE_REQUEST.CONTINUE")}
                    </Button>
                </div>
            </Modal>

        </div>

    )
}

export default FinanceProducts;